/**
 * SessionStorage-backed handoff for the FP Q&A tool.
 *
 * Flow:
 *   1. QATab sets the handoff with the optional clientUserId when an FP
 *      starts a new Q&A, then navigates to /care-navigator.
 *   2. ChatInterface runs the Q&A as usual.
 *   3. On Q&A completion ChatInterface checks for an active handoff. If
 *      present, it POSTs to /financial-advisors/me/qa-engagements with the
 *      session id + clientUserId, clears the handoff, and redirects the FP
 *      back to /dashboard/financial-advisor?fp_qa_engagement=<id>.
 *   4. QATab reads the query param on mount and opens that engagement.
 *
 * sessionStorage (not localStorage) so the handoff is scoped to the tab and
 * cleared when the browser tab closes.
 */

const STORAGE_KEY = 'bquest_fp_qa_handoff';

export type FpQaHandoff = {
  clientUserId: string | null;
  /**
   * FP-chosen plan name set on the ClientPicker. Persisted alongside the
   * handoff so ChatInterface can attach it when posting the engagement.
   */
  name: string | null;
};

export function setFpQaHandoff(handoff: FpQaHandoff): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(handoff));
  } catch {
    // sessionStorage may be unavailable (private browsing, quota); swallow.
  }
}

export function getFpQaHandoff(): FpQaHandoff | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FpQaHandoff;
    if (typeof parsed !== 'object' || parsed === null) return null;
    return {
      clientUserId:
        typeof parsed.clientUserId === 'string' && parsed.clientUserId.length > 0
          ? parsed.clientUserId
          : null,
      name:
        typeof parsed.name === 'string' && parsed.name.trim().length > 0
          ? parsed.name.trim()
          : null,
    };
  } catch {
    return null;
  }
}

export function clearFpQaHandoff(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // swallow
  }
}
